import { useRef, useLayoutEffect } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { ANIMATION, prefersReducedMotion } from './animation-config'

gsap.registerPlugin(ScrollTrigger)

/**
 * Basic scroll-triggered reveal with fade and Y offset
 */
export function useScrollReveal<T extends HTMLElement>(
  selector?: string,
  options?: {
    y?: number
    duration?: number
    stagger?: number
    start?: string
  }
) {
  const ref = useRef<T>(null)

  useLayoutEffect(() => {
    if (!ref.current || prefersReducedMotion()) return

    const ctx = gsap.context(() => {
      const elements = selector ? ref.current!.querySelectorAll(selector) : [ref.current!]

      gsap.from(elements, {
        y: options?.y ?? 30,
        opacity: 0,
        duration: options?.duration ?? 0.7,
        stagger: options?.stagger ?? 0.1,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: ref.current,
          start: options?.start ?? 'top 85%',
          toggleActions: 'play none none none',
        },
      })
    }, ref)

    return () => ctx.revert()
  }, [selector, options?.y, options?.duration, options?.stagger, options?.start])

  return ref
}

/**
 * Premium reveal with blur and scale effect - Creative Bold style
 */
export function useDramaticReveal<T extends HTMLElement>(
  selector?: string,
  options?: {
    y?: number
    blur?: number
    scale?: number
    duration?: number
    stagger?: number
    start?: string
    scrub?: boolean | number
  }
) {
  const ref = useRef<T>(null)
  // Destructure to primitives so an inline `options` object doesn't rebuild the
  // ScrollTrigger every render.
  const {
    y = ANIMATION.distance.long,
    blur = ANIMATION.blur.dramatic,
    scale = ANIMATION.scale.dramatic,
    duration = ANIMATION.duration.slow,
    stagger = ANIMATION.stagger.normal,
    start = 'top 85%',
    scrub = false,
  } = options ?? {}

  useLayoutEffect(() => {
    if (!ref.current || prefersReducedMotion()) return

    const ctx = gsap.context(() => {
      const elements = selector ? ref.current!.querySelectorAll(selector) : [ref.current!]

      gsap.from(elements, {
        y,
        opacity: 0,
        filter: `blur(${blur}px)`,
        scale,
        duration,
        stagger,
        ease: ANIMATION.ease.reveal,
        scrollTrigger: {
          trigger: ref.current,
          start,
          toggleActions: 'play none none none',
          scrub: scrub === true ? 0.5 : scrub,
        },
      })
    }, ref)

    return () => ctx.revert()
  }, [selector, y, blur, scale, duration, stagger, start, scrub])

  return ref
}

/**
 * Grid reveal with stagger pattern
 */
export function useGridReveal<T extends HTMLElement>(
  selector: string,
  options?: {
    y?: number
    blur?: number
    scale?: number
    duration?: number
    stagger?: number
  }
) {
  const ref = useRef<T>(null)
  // Destructure to primitives so an inline `options` object doesn't rebuild the
  // ScrollTrigger every render.
  const {
    y = ANIMATION.distance.normal,
    blur = ANIMATION.blur.normal,
    scale = ANIMATION.scale.normal,
    duration = ANIMATION.duration.normal,
    stagger = 0.4,
  } = options ?? {}

  useLayoutEffect(() => {
    if (!ref.current || prefersReducedMotion()) return

    const ctx = gsap.context(() => {
      const elements = ref.current!.querySelectorAll(selector)

      gsap.from(elements, {
        y,
        opacity: 0,
        filter: `blur(${blur}px)`,
        scale,
        duration,
        stagger: {
          amount: stagger,
          grid: 'auto',
          from: 'start',
        },
        ease: ANIMATION.ease.reveal,
        scrollTrigger: {
          trigger: ref.current,
          start: 'top 85%',
          toggleActions: 'play none none none',
        },
      })
    }, ref)

    return () => ctx.revert()
  }, [selector, y, blur, scale, duration, stagger])

  return ref
}

/**
 * Hero entrance animation (not scroll triggered - runs on mount)
 */
export function useHeroReveal<T extends HTMLElement>(delay = 0.2) {
  const ref = useRef<T>(null)

  useLayoutEffect(() => {
    if (!ref.current || prefersReducedMotion()) return

    const ctx = gsap.context(() => {
      gsap.from(ref.current!.children, {
        y: ANIMATION.distance.short,
        opacity: 0,
        filter: `blur(${ANIMATION.blur.light}px)`,
        scale: ANIMATION.scale.subtle,
        duration: ANIMATION.duration.hero,
        stagger: ANIMATION.stagger.normal,
        ease: ANIMATION.ease.reveal,
        delay,
      })
    }, ref)

    return () => ctx.revert()
  }, [delay])

  return ref
}
